<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="qr-dialog">
      <!-- Header -->
      <div class="dialog-header">
        <div class="header-icon">
          <i class="bi bi-qr-code"></i>
        </div>
        <div class="header-text">
          <h2>{{ $t("chat.my_qr_code") || "My QR Code" }}</h2>
          <span class="app-badge">Academia</span>
        </div>
        <button class="close-btn" @click="$emit('close')">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <!-- Instruction -->
      <div class="instruction-bar">
        <i class="bi bi-info-circle"></i>
        <span>{{
          $t("chat.scan_qr_instruction") ||
          "Scan this code to add me as a friend"
        }}</span>
      </div>

      <!-- QR Code Container -->
      <div class="qr-container">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <span>{{ $t("common.loading") || "Loading..." }}</span>
        </div>
        <div v-else-if="error" class="error-state">
          <i class="bi bi-exclamation-triangle"></i>
          <span>{{ error }}</span>
          <button class="retry-btn" @click="loadShareableId">
            <i class="bi bi-arrow-repeat"></i>
            {{ $t("common.retry") || "Retry" }}
          </button>
        </div>
        <div v-else class="qr-code-wrapper">
          <canvas ref="qrCanvas" class="qr-canvas"></canvas>
          <div class="shareable-id">{{ shareableId }}</div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button
          class="action-btn copy-btn"
          @click="copyToClipboard"
          :disabled="!shareableId"
        >
          <i class="bi bi-clipboard"></i>
          <span>{{
            copied
              ? $t("chat.copied_to_clipboard") || "Copied!"
              : $t("common.copy") || "Copy"
          }}</span>
        </button>
        <button
          class="action-btn share-btn"
          @click="shareId"
          :disabled="!shareableId"
        >
          <i class="bi bi-share"></i>
          <span>{{ $t("chat.share") || "Share" }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ChatService } from "@/services/chat";
import QRCode from "qrcode";

export default {
  name: "QrCodeDialog",
  emits: ["close"],
  data() {
    return {
      loading: true,
      error: null,
      shareableId: null,
      copied: false,
    };
  },
  async mounted() {
    await this.loadShareableId();
  },
  methods: {
    async loadShareableId() {
      this.loading = true;
      this.error = null;

      try {
        const id = await ChatService.getShareableId();
        if (!id) {
          throw new Error(
            this.$t("chat.failed_to_load_qr_code") || "Failed to load QR code"
          );
        }
        this.shareableId = id;

        // First set loading to false so canvas renders
        this.loading = false;

        // Wait for DOM to update, then generate QR code
        await this.$nextTick();
        // Additional delay to ensure canvas is fully rendered
        setTimeout(() => {
          this.generateQRCode();
        }, 100);
      } catch (e) {
        this.error =
          e?.message ||
          this.$t("chat.failed_to_load_qr_code") ||
          "Failed to load QR code";
        this.loading = false;
      }
    },

    async generateQRCode() {
      if (!this.shareableId || !this.$refs.qrCanvas) return;

      try {
        await QRCode.toCanvas(this.$refs.qrCanvas, this.shareableId, {
          width: 250,
          margin: 2,
          color: {
            dark: "#1a1a2e",
            light: "#ffffff",
          },
        });
      } catch (e) {
        console.error("[QrCodeDialog] Failed to generate QR code:", e);
        this.error =
          this.$t("chat.failed_to_load_qr_code") ||
          "Failed to generate QR code";
      }
    },

    async copyToClipboard() {
      if (!this.shareableId) return;

      try {
        await navigator.clipboard.writeText(this.shareableId);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (e) {
        console.error("[QrCodeDialog] Failed to copy:", e);
      }
    },

    async shareId() {
      if (!this.shareableId) {
        console.warn("[QrCodeDialog] Cannot share - shareableId is empty");
        return;
      }

      console.log("[QrCodeDialog] Sharing ID:", this.shareableId);

      // Build share text by appending ID directly (more reliable)
      const baseText =
        this.$t("chat.share_qr_message") || "Add me on Academia Chat! My ID:";
      const shareText = `${baseText} ${this.shareableId}`;

      console.log("[QrCodeDialog] Share text:", shareText);

      if (navigator.share) {
        try {
          await navigator.share({
            title: this.$t("chat.add_me_on_chat") || "Add me on Academia Chat",
            text: shareText,
          });
        } catch (e) {
          console.log(
            "[QrCodeDialog] Share cancelled or failed, copying instead"
          );
          // User cancelled or share failed - fallback to copy
          await this.copyToClipboard();
        }
      } else {
        // Fallback to copy
        await this.copyToClipboard();
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.qr-dialog {
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 380px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease-out;
  overflow: hidden;
}

/* Header */
.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: white;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon i {
  font-size: 1.5rem;
  color: #ffffff !important;
}

.header-text {
  flex: 1;
}

.header-text h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.app-badge {
  font-size: 0.75rem;
  opacity: 0.9;
}

.close-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: #ffffff !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.close-btn i {
  color: #ffffff !important;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Instruction */
.instruction-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #e8fff1;
  color: #00a838;
  font-size: 0.875rem;
}

.instruction-bar i {
  font-size: 1rem;
}

/* QR Container */
.qr-container {
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 300px;
  justify-content: center;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #64748b;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #00c444;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-state i {
  font-size: 2rem;
  color: #ef4444;
}

.retry-btn {
  margin-top: 8px;
  padding: 8px 16px;
  background: #00c444;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
}

.retry-btn:hover {
  background: #00a838;
}

.qr-code-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.qr-canvas {
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.shareable-id {
  font-family: monospace;
  font-size: 0.75rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 8px 16px;
  border-radius: 8px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e2e8f0;
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.copy-btn {
  background: #f1f5f9;
  color: #1a1a2e;
}

.copy-btn:hover:not(:disabled) {
  background: #e2e8f0;
}

.share-btn {
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: #ffffff !important;
}

.share-btn i {
  color: #ffffff !important;
}

.share-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.3);
}

/* RTL Support */
[dir="rtl"] .dialog-header {
  flex-direction: row-reverse;
}

[dir="rtl"] .instruction-bar {
  flex-direction: row-reverse;
}

[dir="rtl"] .action-btn {
  flex-direction: row-reverse;
}
</style>
